import React, { useState } from "react";
import { Sparkles, MessageSquare, Zap } from "lucide-react";
import { Layout } from "../components/layout";
import { SEO } from "../components/common/SEO";
import { Link } from "react-router-dom";
import {
  PricingCard,
  CurrencySelector,
  PricingFAQ,
  Currency,
  currencySymbols,
  exchangeRates,
} from "../components/pricing";

interface Plan {
  name: string;
  price: number;
  requests: number;
  features: string[];
  popular?: boolean;
}

const plans: Plan[] = [
  {
    name: "Basic",
    price: 19.99,
    requests: 100,
    features: [
      "Generate block patterns with AI",
      "100 requests",
      "Use on unlimited WordPress sites",
      "Access to all block templates",
      "Customer support",
      "Save patterns to library",
    ],
  },
  {
    name: "Professional",
    price: 89.99,
    requests: 500,
    features: [
      "Everything in Basic, plus:",
      "500 requests",
      "Priority customer support",
      "Advanced customization options",
      "Export in multiple formats",
      "Team collaboration features",
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    price: 199.99,
    requests: 2000,
    features: [
      "Everything in Professional, plus:",
      "2,000 requests",
      "Dedicated account manager",
      "Custom block development",
      "White-label option",
      "API access",
      "Premium support",
    ],
  },
];

export function PricingPage() {
  const [currency, setCurrency] = useState<Currency>("USD");

  const formatPrice = (price: number) => {
    const convertedPrice = price * exchangeRates[currency];
    return convertedPrice.toFixed(2);
  };

  return (
    <Layout>
      <SEO
        title="Pricing | Block Pattern Generator Plans"
        description="Choose the perfect plan for your WordPress block pattern needs. From individual creators to agencies, we have flexible pricing options for everyone."
        keywords={[
          "wordpress pricing",
          "block pattern plans",
          "pattern generator subscription",
          "wordpress blocks pricing",
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the plan that's right for you and start creating beautiful
            WordPress block patterns today.
          </p>
        </div>

        <CurrencySelector currency={currency} setCurrency={setCurrency} />

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan) => (
            <PricingCard
              key={plan.name}
              name={plan.name}
              price={plan.price}
              requests={plan.requests}
              features={plan.features}
              popular={plan.popular}
              currencySymbol={currencySymbols[currency]}
              formattedPrice={formatPrice(plan.price)}
              onSelectPlan={() => console.log(`Selected ${plan.name} plan`)}
            />
          ))}
        </div>

        <div className="max-w-3xl mx-auto bg-white/5 rounded-xl p-8 mb-16">
          <div className="flex items-center justify-center mb-6">
            <Sparkles className="h-8 w-8 text-volt-blue mr-3" />
            <h2 className="text-2xl font-bold text-white">
              Enterprise Solutions
            </h2>
          </div>
          <p className="text-gray-400 text-center mb-6">
            Need a custom solution for your agency or large team? We offer
            tailored enterprise plans with additional features and support.
          </p>
          <div className="flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 bg-volt-blue text-white rounded-lg font-medium hover:bg-volt-blue/90 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white/5 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <MessageSquare className="h-8 w-8 text-volt-blue mr-3" />
              <h2 className="text-xl font-bold text-white">
                24/7 Customer Support
              </h2>
            </div>
            <p className="text-gray-400 mb-6">
              Our dedicated support team is available around the clock to help
              you with any questions or issues you may encounter.
            </p>
            <Link
              to="/contact"
              className="text-volt-blue hover:text-volt-green transition-colors"
            >
              Contact Support →
            </Link>
          </div>

          <div className="bg-white/5 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <Zap className="h-8 w-8 text-volt-blue mr-3" />
              <h2 className="text-xl font-bold text-white">Upgrade Anytime</h2>
            </div>
            <p className="text-gray-400 mb-6">
              Start with any plan and upgrade as your needs grow. We'll prorate
              your existing subscription and apply it to your new plan.
            </p>
            <Link
              to="/docs/billing"
              className="text-volt-blue hover:text-volt-green transition-colors"
            >
              Learn About Billing →
            </Link>
          </div>
        </div>

        <PricingFAQ />
      </div>
    </Layout>
  );
}
